/**
 * MarkdownV2 — react-markdown-based renderer with v10 design grammar
 * overrides. Replacement for the hand-rolled Markdown.jsx that was
 * causing post-completion render hangs on long briefs (16K+ chars).
 *
 * Bug history:
 *   - Markdown.jsx hand-rolled per-line parser + nested component tree
 *     (HedgeMark, MentionHover wraps) saturated the main thread on
 *     post-completion render. CDP eval froze for 60+ seconds; even
 *     screenshots timed out. See /shared/V1_CM_Tier2_Migration_Addendum.md
 *     and the diagnostic capture session 2026-05-09.
 *   - react-markdown is battle-tested at scale (used by GitHub for
 *     READMEs, ChatGPT for chat output, etc.); parses AST once via
 *     remark + walks once via the React reconciler. No per-text-node
 *     regex, no nested wrapping.
 *
 * Design overrides preserved from v10:
 *   - h2 with "Section N — Title" pattern → SectionHeader(index, title)
 *   - h2 plain → bordered SectionHeader-style heading
 *   - h1 → display heading with navy color + tight letter-spacing
 *   - p → Prose wrapper (typography defaults)
 *   - ul / ol / li → v10 list styling (no CheckList/NumberedList wrap;
 *     visual parity via inline styles to avoid the children-shape
 *     translation Custom-component lists require)
 *   - blockquote with [!Tip] / [!WatchOut] / [!Remember] / [!Key] markers
 *     → corresponding Callout variant
 *   - blockquote without marker → Callout.Remember (V10 default)
 *   - GFM tables (via remark-gfm) → styled <table>/<thead>/<tbody>/<tr>/<td>
 *
 * Polish gaps (acceptable for ship):
 *   - HedgeMark wrapping for hedge phrases — not implemented; hedge
 *     phrases render as plain text. Follow-up via remark plugin if
 *     warranted.
 *   - MentionHover wrapping for @-tokens — not implemented; @-tokens
 *     render as plain text. Follow-up if interactive previews are
 *     needed in the printed brief surface.
 */

import { useMemo } from "react";
import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import SectionHeader from "./SectionHeader.jsx";
import Callout from "./Callout.jsx";

const SECTION_HEADER_PATTERN = /^Section\s+(\d+)\s*[—–-]\s*(.+)$/i;

// react-markdown turns blockquotes into <blockquote>{children}</blockquote>.
// children are the parsed paragraph nodes inside. To detect [!Tip] etc.
// markers, we walk the children to extract the leading text content,
// match the marker, and dispatch to the corresponding Callout variant.
function extractLeadingText(children) {
  if (typeof children === "string") return children;
  if (Array.isArray(children)) {
    for (const c of children) {
      const t = extractLeadingText(c);
      if (t) return t;
    }
    return "";
  }
  if (children && typeof children === "object" && children.props) {
    return extractLeadingText(children.props.children);
  }
  return "";
}

// Strip the "[!Marker]" prefix from the first text node in children. Used
// after detecting a callout marker so we don't render the marker itself
// inside the Callout body.
function stripMarker(children, marker) {
  if (!marker) return children;
  let stripped = false;
  const strip = (node) => {
    if (stripped) return node;
    if (typeof node === "string") {
      const re = new RegExp(`^\\s*\\[!${marker}\\]\\s*`, "i");
      const out = node.replace(re, "");
      if (out !== node) stripped = true;
      return out;
    }
    if (Array.isArray(node)) {
      return node.map(strip);
    }
    if (node && typeof node === "object" && node.props) {
      return {
        ...node,
        props: { ...node.props, children: strip(node.props.children) },
      };
    }
    return node;
  };
  return strip(children);
}

// ─────────────────────────────────────────────────────────────────────
// Name auto-bold (#80) — wrap subject-name occurrences in <strong> for
// skim-readability. Per Russ 2026-05-09 product call: HR managers
// reading 12-section briefs benefit from name anchoring; CM briefs have
// heavy two-name back-and-forth that benefits even more.
//
// Scope:
//   - Wrap only in p / li / td (NOT th, NOT headings, NOT code)
//   - Skip inside existing <strong>: don't double-wrap (substrate's
//     existing bold for emphasis already covers those names)
//   - Recurse into <em> so italicized prose with names still gets bold
//   - Word-boundary regex with possessive support: \b(Name)('s|s')?\b
//   - Names sorted longest-first so "Russell" matches before "Russ"
//   - Visual treatment matches the substrate-emitted bold (navy-tinted,
//     fontWeight 700) so the page reads as one cohesive bold layer.
// ─────────────────────────────────────────────────────────────────────

function buildNamesRegex(names) {
  if (!Array.isArray(names) || names.length === 0) return null;
  const cleaned = Array.from(
    new Set(
      names
        .map((n) => (typeof n === "string" ? n.trim() : ""))
        .filter((n) => n.length >= 2),
    ),
  )
    .map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
    .sort((a, b) => b.length - a.length);
  if (cleaned.length === 0) return null;
  // Match name optionally followed by 's or s' (possessives). Word
  // boundaries on both sides so "Russell" doesn't match "Russ".
  return new RegExp(`\\b(${cleaned.join("|")})('s|s')?\\b`, "g");
}

function splitAndWrapNames(text, namesRe, keyPrefix) {
  namesRe.lastIndex = 0;
  const parts = [];
  let lastIdx = 0;
  let key = 0;
  let m;
  while ((m = namesRe.exec(text)) !== null) {
    if (m.index > lastIdx) parts.push(text.slice(lastIdx, m.index));
    parts.push(
      <strong
        key={`${keyPrefix}-${key++}`}
        style={{ fontWeight: 600, color: "var(--ppr-color-text-primary)" }}
      >
        {m[0]}
      </strong>,
    );
    lastIdx = m.index + m[0].length;
    if (m.index === namesRe.lastIndex) namesRe.lastIndex++; // zero-length safety
  }
  if (lastIdx < text.length) parts.push(text.slice(lastIdx));
  if (parts.length === 0) return text;
  if (parts.length === 1 && typeof parts[0] === "string") return parts[0];
  return parts;
}

function wrapNamesInChildren(children, namesRe, keyPrefix = "n") {
  if (!namesRe) return children;
  if (typeof children === "string") {
    return splitAndWrapNames(children, namesRe, keyPrefix);
  }
  if (Array.isArray(children)) {
    return children.map((c, i) => wrapNamesInChildren(c, namesRe, `${keyPrefix}-${i}`));
  }
  if (React.isValidElement(children)) {
    // Skip already-bolded or code spans — don't double-wrap or interfere
    // with code formatting. Recurse into <em> so italicized prose with
    // names still gets the bold treatment.
    if (children.type === "strong" || children.type === "code") {
      return children;
    }
    return React.cloneElement(
      children,
      {},
      wrapNamesInChildren(children.props.children, namesRe, keyPrefix),
    );
  }
  return children;
}

function buildComponentOverrides(namesRe) { return {
  // ── Headings ────────────────────────────────────────────────────────
  h1: ({ children }) => (
    <h1
      style={{
        fontFamily: "var(--ppr-typography-font-family-display)",
        fontSize: "var(--ppr-typography-font-size-3xl)",
        fontWeight: "var(--ppr-typography-font-weight-regular)",
        color: "var(--ppr-color-text-primary)",
        letterSpacing: "var(--ppr-typography-letter-spacing-snug)",
        margin: "0 0 22px",
        lineHeight: "var(--ppr-typography-line-height-tight)",
      }}
    >
      {children}
    </h1>
  ),

  h2: ({ children }) => {
    const text = extractLeadingText(children);
    // "Your move" / "Pocket card" capstones get the distinctive closing
    // treatment — a crimson kicker over a crimson top rule — so the single
    // next action reads as the punctuation mark at the end of the read.
    const capstone = text.trim().toLowerCase().replace(/[:.]+$/, "");
    if (capstone === "your move" || capstone === "pocket card" || capstone === "the pocket card") {
      return (
        <div
          style={{
            marginTop: 30,
            paddingTop: 16,
            borderTop: "2px solid var(--ppr-color-accent-coral)",
          }}
        >
          <div
            style={{
              fontFamily: "var(--ppr-typography-font-family-ui)",
              fontSize: 11,
              letterSpacing: "0.16em",
              textTransform: "uppercase",
              color: "var(--ppr-color-accent-coral)",
            }}
          >
            {children}
          </div>
        </div>
      );
    }
    const m = text.match(SECTION_HEADER_PATTERN);
    if (m) {
      return <SectionHeader index={parseInt(m[1], 10)} title={m[2].trim()} />;
    }
    return (
      <h2
        style={{
          fontFamily: "var(--ppr-typography-font-family-display)",
          fontSize: "var(--ppr-typography-font-size-xl)",
          fontWeight: "var(--ppr-typography-font-weight-regular)",
          color: "var(--ppr-color-text-primary)",
          letterSpacing: "var(--ppr-typography-letter-spacing-snug)",
          margin: "32px 0 14px",
          paddingBottom: 10,
          borderBottom: "1px solid var(--ppr-color-border-default)",
        }}
      >
        {children}
      </h2>
    );
  },

  h3: ({ children }) => (
    <h3
      style={{
        fontFamily: "var(--ppr-typography-font-family-display)",
        fontSize: "var(--ppr-typography-font-size-md)",
        fontWeight: "var(--ppr-typography-font-weight-regular)",
        color: "var(--ppr-color-text-primary)",
        margin: "24px 0 10px",
      }}
    >
      {children}
    </h3>
  ),

  // ── Paragraphs + lists ──────────────────────────────────────────────
  p: ({ children }) => (
    <p
      style={{
        fontFamily: "var(--ppr-typography-font-family-body)",
        fontSize: "var(--ppr-typography-font-size-base)",
        lineHeight: 1.65,
        color: "var(--ppr-color-text-primary)",
        margin: "0 0 14px",
      }}
    >
      {wrapNamesInChildren(children, namesRe, "p")}
    </p>
  ),

  ul: ({ children }) => (
    <ul style={{ margin: "0 0 14px 0", paddingLeft: 22 }}>{children}</ul>
  ),

  ol: ({ children }) => (
    <ol style={{ margin: "0 0 14px 0", paddingLeft: 22 }}>{children}</ol>
  ),

  li: ({ children }) => (
    <li
      style={{
        fontFamily: "var(--ppr-typography-font-family-body)",
        fontSize: "var(--ppr-typography-font-size-base)",
        lineHeight: 1.6,
        color: "var(--ppr-color-text-primary)",
        marginBottom: 6,
      }}
    >
      {wrapNamesInChildren(children, namesRe, "li")}
    </li>
  ),

  // ── Blockquotes → Callout variants ──────────────────────────────────
  // Markers (case-insensitive): [!Tip], [!Watch] / [!WatchOut],
  // [!Remember], [!Key] / [!KeyInsight]. No marker → Callout.Remember.
  blockquote: ({ children }) => {
    const lead = extractLeadingText(children);
    const markerMatch = lead.match(/^\s*\[!(\w+)\]/);
    const marker = markerMatch ? markerMatch[1] : null;
    const lower = (marker || "").toLowerCase();
    let Variant = Callout.Remember;
    if (lower === "tip") Variant = Callout.Tip;
    else if (lower === "watch" || lower === "watchout") Variant = Callout.WatchOut;
    else if (lower === "key" || lower === "keyinsight") Variant = Callout.KeyInsight;
    const stripped = stripMarker(children, marker);
    return <Variant>{stripped}</Variant>;
  },

  // ── Inline emphasis ─────────────────────────────────────────────────
  strong: ({ children }) => (
    <strong style={{ fontWeight: 600, color: "var(--ppr-color-text-primary)" }}>
      {children}
    </strong>
  ),
  em: ({ children }) => <em style={{ fontStyle: "italic" }}>{children}</em>,

  // ── GFM tables ──────────────────────────────────────────────────────
  table: ({ children }) => (
    <div style={{ overflowX: "auto", margin: "0 0 16px" }}>
      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          fontFamily: "var(--ppr-typography-font-family-body)",
          fontSize: "var(--ppr-typography-font-size-sm)",
        }}
      >
        {children}
      </table>
    </div>
  ),
  thead: ({ children }) => (
    <thead
      style={{
        background: "var(--ppr-color-background-sub)",
        borderBottom: "1px solid var(--ppr-color-border-strong)",
      }}
    >
      {children}
    </thead>
  ),
  tbody: ({ children }) => <tbody>{children}</tbody>,
  tr: ({ children }) => (
    <tr style={{ borderBottom: "1px solid var(--ppr-color-border-default)" }}>
      {children}
    </tr>
  ),
  th: ({ children }) => (
    <th
      style={{
        padding: "10px 12px",
        textAlign: "left",
        fontFamily: "var(--ppr-typography-font-family-ui)",
        fontSize: "var(--ppr-typography-font-size-xs)",
        textTransform: "uppercase",
        letterSpacing: "var(--ppr-typography-letter-spacing-wider)",
        color: "var(--ppr-color-text-secondary)",
      }}
    >
      {children}
    </th>
  ),
  td: ({ children }) => (
    <td
      style={{
        padding: "10px 12px",
        verticalAlign: "top",
        color: "var(--ppr-color-text-primary)",
      }}
    >
      {wrapNamesInChildren(children, namesRe, "td")}
    </td>
  ),

  // ── Horizontal rule ─────────────────────────────────────────────────
  hr: () => (
    <hr
      style={{
        border: "none",
        borderTop: "1px solid var(--ppr-color-border-default)",
        margin: "24px 0",
      }}
    />
  ),

  // ── Code (rare in briefs but defensive) ─────────────────────────────
  code: ({ children, inline }) =>
    inline ? (
      <code
        style={{
          fontFamily: "var(--ppr-typography-font-family-mono)",
          fontSize: "0.9em",
          background: "var(--ppr-color-background-muted)",
          padding: "1px 5px",
          borderRadius: 3,
        }}
      >
        {children}
      </code>
    ) : (
      <pre
        style={{
          fontFamily: "var(--ppr-typography-font-family-mono)",
          fontSize: "var(--ppr-typography-font-size-sm)",
          background: "var(--ppr-color-background-muted)",
          padding: "12px 14px",
          borderRadius: 4,
          overflowX: "auto",
          margin: "0 0 14px",
        }}
      >
        <code>{children}</code>
      </pre>
    ),
}; }

const REMARK_PLUGINS = [remarkGfm];

/**
 * @param {object} props
 * @param {string} props.source — markdown text
 * @param {string[]} [props.subjectNames] — optional subject first names to
 *   auto-bold throughout the rendered output (#80 skim affordance). When
 *   omitted, no name-bolding applied.
 */
export default function MarkdownV2({ source, subjectNames }) {
  const namesRe = useMemo(() => buildNamesRegex(subjectNames), [subjectNames]);
  const overrides = useMemo(() => buildComponentOverrides(namesRe), [namesRe]);
  return (
    <div data-component="MarkdownV2">
      <ReactMarkdown
        remarkPlugins={REMARK_PLUGINS}
        components={overrides}
      >
        {source || ""}
      </ReactMarkdown>
    </div>
  );
}
